import * as THREE from 'three'
import type { TrackPath } from './trackImporter'

// TODO: Whole thing could use a refactor to simplify

export type TrackTransitionPointRange = {
    startPoint: number
    endPoint: number
}

export type TrackRegionConfig = {
    transition1: TrackTransitionPointRange
    transition2: TrackTransitionPointRange
    transition3: TrackTransitionPointRange
}

type ResolvedTrackTransitionRange = {
    start: number
    end: number
}

export type ResolvedTrackRegionConfig = {
    transition1: ResolvedTrackTransitionRange
    transition2: ResolvedTrackTransitionRange
    transition3: ResolvedTrackTransitionRange
}

export type TrackRegionWeight = {
    id: 1 | 2 | 3 | 4
    weight: number
}

export type TrackRegionState = {
    activeRegion: 1 | 2 | 3 | 4
    activeTransition: boolean
    transitionProgress: number
    trackPosition: number
    regions: [
        TrackRegionWeight,
        TrackRegionWeight,
        TrackRegionWeight,
        TrackRegionWeight,
    ]
}

type TrackRegionId = TrackRegionState['activeRegion']
type TrackRegionWeights = [number, number, number, number]
type TrackRegionTransitionDefinition = {
    from: Exclude<TrackRegionId, 4>
    to: Exclude<TrackRegionId, 1>
    range: ResolvedTrackTransitionRange
}

export function resolveTrackRegionConfig(
    regionConfig: TrackRegionConfig,
    trackPath: TrackPath,
): ResolvedTrackRegionConfig {
    if (trackPath.resolvedRegionConfig) {
        validateResolvedTrackRegionConfig(trackPath.resolvedRegionConfig)
        return trackPath.resolvedRegionConfig
    }

    const normalized = {
        transition1: resolveTrackTransitionRange(
            regionConfig.transition1,
            trackPath,
        ),
        transition2: resolveTrackTransitionRange(
            regionConfig.transition2,
            trackPath,
        ),
        transition3: resolveTrackTransitionRange(
            regionConfig.transition3,
            trackPath,
        ),
    }

    validateResolvedTrackRegionConfig(normalized)
    return normalized
}

export function getTrackRegionStateAtPosition(
    trackPosition: number,
    resolvedRegions: ResolvedTrackRegionConfig,
    trackLength: number,
): TrackRegionState {
    const position = normalizeTrackPosition(trackPosition, trackLength)
    const transitions = createTrackRegionTransitions(resolvedRegions)

    for (const transition of transitions) {
        if (position < transition.range.start) {
            return createTrackRegionState(
                position,
                transition.from,
                false,
                0,
                weightsForRegion(transition.from),
            )
        }

        if (position <= transition.range.end) {
            const progress = inverseLerpRange(transition.range, position)

            return createTrackRegionState(
                position,
                transition.from,
                true,
                progress,
                weightsForTransition(
                    transition.from,
                    transition.to,
                    progress,
                ),
            )
        }
    }

    return createTrackRegionState(position, 4, false, 0, weightsForRegion(4))
}

function resolveTrackTransitionRange(
    range: TrackTransitionPointRange,
    trackPath: TrackPath,
): ResolvedTrackTransitionRange {
    return {
        start: getTrackPositionForAnchorPoint(range.startPoint, trackPath),
        end: getTrackPositionForAnchorPoint(range.endPoint, trackPath),
    }
}

function getTrackPositionForAnchorPoint(
    pointIndex: number,
    trackPath: TrackPath,
): number {
    if (pointIndex >= trackPath.segments.length) {
        return trackPath.pathEndDistance
    }

    const sample = trackPath.lengthSamples.find(
        (entry) => entry.segmentIndex === pointIndex && entry.t === 0,
    )

    if (!sample) {
        throw new Error(`Could not resolve track anchor point ${pointIndex}.`)
    }

    return sample.distance
}

function validateResolvedTrackRegionConfig(
    regionConfig: ResolvedTrackRegionConfig,
) {
    const orderedBoundaries = [
        regionConfig.transition1.start,
        regionConfig.transition1.end,
        regionConfig.transition2.start,
        regionConfig.transition2.end,
        regionConfig.transition3.start,
        regionConfig.transition3.end,
    ]

    for (let index = 1; index < orderedBoundaries.length; index += 1) {
        if (orderedBoundaries[index - 1] > orderedBoundaries[index]) {
            throw new Error(
                'Track transitions must be ordered from transition1.start through transition3.end.',
            )
        }
    }
}

function createTrackRegionState(
    trackPosition: number,
    activeRegion: TrackRegionId,
    activeTransition: boolean,
    transitionProgress: number,
    weights: TrackRegionWeights,
): TrackRegionState {
    return {
        activeRegion,
        activeTransition,
        transitionProgress,
        trackPosition,
        regions: [
            { id: 1, weight: weights[0] },
            { id: 2, weight: weights[1] },
            { id: 3, weight: weights[2] },
            { id: 4, weight: weights[3] },
        ],
    }
}

function inverseLerpRange(
    range: ResolvedTrackTransitionRange,
    position: number,
): number {
    const span = Math.max(range.end - range.start, 0.000001)
    return THREE.MathUtils.clamp((position - range.start) / span, 0, 1)
}

function createTrackRegionTransitions(
    resolvedRegions: ResolvedTrackRegionConfig,
): TrackRegionTransitionDefinition[] {
    return [
        { from: 1, to: 2, range: resolvedRegions.transition1 },
        { from: 2, to: 3, range: resolvedRegions.transition2 },
        { from: 3, to: 4, range: resolvedRegions.transition3 },
    ]
}

function weightsForRegion(activeRegion: TrackRegionId): TrackRegionWeights {
    return [
        activeRegion === 1 ? 1 : 0,
        activeRegion === 2 ? 1 : 0,
        activeRegion === 3 ? 1 : 0,
        activeRegion === 4 ? 1 : 0,
    ]
}

function weightsForTransition(
    fromRegion: Exclude<TrackRegionId, 4>,
    toRegion: Exclude<TrackRegionId, 1>,
    progress: number,
): TrackRegionWeights {
    const weights: TrackRegionWeights = [0, 0, 0, 0]

    weights[fromRegion - 1] = 1 - progress
    weights[toRegion - 1] = progress

    return weights
}

function normalizeTrackPosition(position: number, trackLength: number): number {
    const wrappedPosition = THREE.MathUtils.euclideanModulo(
        position,
        trackLength,
    )

    return wrappedPosition === 0 && position > 0
        ? trackLength
        : wrappedPosition
}
